(function () {
  const scrollHandlers = new WeakMap();
  const stickyClickHandlers = new WeakMap();
  const observers = new Map();

  function getVariantSwitcher(sectionId) {
    return (
      document.querySelector(`variant-radios[data-section="${sectionId}"]`) ||
      document.querySelector(`variant-selects[data-section="${sectionId}"]`)
    );
  }

  function getOptionNameFromFieldset(fieldset) {
    const label = fieldset.querySelector('[data-variant-picker-label]');
    if (label) return label.getAttribute('data-variant-picker-label');

    const legend = fieldset.querySelector('legend');
    if (!legend) return '';

    return legend.textContent.split(':')[0].trim();
  }

  function getOptionNameFromSelect(select) {
    const label = select.closest('.product-form__input')?.querySelector('.form__label');
    return label ? label.textContent.trim() : '';
  }

  function getMissingOptions(variantSwitcher) {
    if (!variantSwitcher) return [];

    const missing = [];

    if (variantSwitcher.tagName === 'VARIANT-RADIOS') {
      variantSwitcher.querySelectorAll('fieldset').forEach((fieldset) => {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (!checked) missing.push(getOptionNameFromFieldset(fieldset));
      });
      return missing.filter(Boolean);
    }

    if (variantSwitcher.tagName === 'VARIANT-SELECTS') {
      variantSwitcher.querySelectorAll('select').forEach((select) => {
        if (!select.value) missing.push(getOptionNameFromSelect(select));
      });
      return missing.filter(Boolean);
    }

    return [];
  }

  function getCurrentVariant(variantSwitcher) {
    if (!variantSwitcher || typeof variantSwitcher.getVariantData !== 'function') return null;

    const options =
      variantSwitcher.tagName === 'VARIANT-RADIOS'
        ? Array.from(variantSwitcher.querySelectorAll('fieldset')).map((fieldset) => {
            const checked = fieldset.querySelector('input[type="radio"]:checked');
            return checked ? checked.value : null;
          })
        : Array.from(variantSwitcher.querySelectorAll('select'), (select) => select.value);

    if (options.some((option) => !option)) return null;

    return variantSwitcher.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => options[index] === option)
        .includes(false);
    });
  }

  function getSelectLabel(missingOptions) {
    const strings = window.variantStrings || {};

    if (missingOptions.length > 1) return strings.selectOptions || 'Select options';

    const name = missingOptions[0] || '';

    if (/color/i.test(name)) return strings.selectColor || 'Select color';
    if (/size/i.test(name)) return strings.selectSize || 'Select size';

    return strings.selectOptions || 'Select options';
  }

  function getAddToCartLabel(sectionId) {
    const strings = window.variantStrings || {};
    const productFormEl = document
      .getElementById(`product-form-${sectionId}`)
      ?.closest('product-form');

    if (productFormEl?.dataset.isBundle === 'true') {
      return strings.addBundleToCart || 'Add bundle to cart';
    }

    return strings.addToCart || 'Add to cart';
  }

  function getFirstMissingTarget(variantSwitcher, missingOptions) {
    if (!variantSwitcher || !missingOptions.length) return null;

    const firstMissing = missingOptions[0];

    if (variantSwitcher.tagName === 'VARIANT-RADIOS') {
      for (const fieldset of variantSwitcher.querySelectorAll('fieldset')) {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (!checked) return fieldset;
        if (getOptionNameFromFieldset(fieldset) === firstMissing) return fieldset;
      }
    }

    if (variantSwitcher.tagName === 'VARIANT-SELECTS') {
      for (const select of variantSwitcher.querySelectorAll('select')) {
        if (!select.value || getOptionNameFromSelect(select) === firstMissing) {
          return select.closest('.product-form__input') || select;
        }
      }
    }

    return null;
  }

  function scrollToMissingOption(sectionId) {
    const variantSwitcher = getVariantSwitcher(sectionId);
    const missingOptions = getMissingOptions(variantSwitcher);
    const target = getFirstMissingTarget(variantSwitcher, missingOptions);

    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function bindSelectClick(button, sectionId) {
    const existing = scrollHandlers.get(button);
    if (existing) button.removeEventListener('click', existing);

    const handler = (event) => {
      event.preventDefault();
      scrollToMissingOption(sectionId);
    };

    scrollHandlers.set(button, handler);
    button.addEventListener('click', handler);
  }

  function unbindSelectClick(button) {
    const existing = scrollHandlers.get(button);
    if (existing) {
      button.removeEventListener('click', existing);
      scrollHandlers.delete(button);
    }
  }

  function bindStickyAddClick(stickyButton, mainButton) {
    const existing = stickyClickHandlers.get(stickyButton);
    if (existing) stickyButton.removeEventListener('click', existing);

    const handler = (event) => {
      event.preventDefault();
      if (!mainButton.disabled) mainButton.click();
    };

    stickyClickHandlers.set(stickyButton, handler);
    stickyButton.addEventListener('click', handler);
  }

  function unbindStickyAddClick(stickyButton) {
    const existing = stickyClickHandlers.get(stickyButton);
    if (existing) {
      stickyButton.removeEventListener('click', existing);
      stickyClickHandlers.delete(stickyButton);
    }
  }

  function applyCtaState(button, labelEl, state, label, sectionId, mainButton) {
    if (!button || !labelEl) return;

    button.dataset.ctaMode = state.mode;
    labelEl.textContent = label;

    if (state.mode === 'select') {
      button.type = 'button';
      button.removeAttribute('disabled');
      button.removeAttribute('aria-disabled');
      bindSelectClick(button, sectionId);
      if (button !== mainButton) unbindStickyAddClick(button);
      return;
    }

    unbindSelectClick(button);

    if (state.mode === 'add') {
      button.type = button === mainButton ? 'submit' : 'button';
      button.removeAttribute('disabled');
      button.removeAttribute('aria-disabled');
      if (button !== mainButton) bindStickyAddClick(button, mainButton);
      return;
    }

    button.type = 'button';
    button.setAttribute('disabled', 'disabled');
    if (button !== mainButton) unbindStickyAddClick(button);
  }

  function resolveCtaState(sectionId) {
    const pickupAvailability = document.querySelector('pickup-availability');
    const hasOnlyDefaultVariant =
      pickupAvailability?.dataset.hasOnlyDefaultVariant === 'true';

    if (hasOnlyDefaultVariant) {
      const hiddenInput = document.querySelector(`#product-form-${sectionId} input[name="id"]`);
      const variantId = hiddenInput ? Number(hiddenInput.value) : null;
      const variantSwitcher = getVariantSwitcher(sectionId);
      let currentVariant = null;

      if (variantSwitcher && variantId) {
        currentVariant = variantSwitcher.getVariantData().find((variant) => variant.id === variantId);
      }

      if (currentVariant && !currentVariant.available) {
        return { mode: 'sold-out', label: window.variantStrings?.soldOut || 'Sold out' };
      }

      return { mode: 'add', label: getAddToCartLabel(sectionId) };
    }

    const variantSwitcher = getVariantSwitcher(sectionId);
    const missingOptions = getMissingOptions(variantSwitcher);

    if (missingOptions.length) {
      return { mode: 'select', label: getSelectLabel(missingOptions) };
    }

    const currentVariant = getCurrentVariant(variantSwitcher);

    if (!currentVariant) {
      return { mode: 'unavailable', label: window.variantStrings?.unavailable || 'Unavailable' };
    }

    if (!currentVariant.available) {
      return { mode: 'sold-out', label: window.variantStrings?.soldOut || 'Sold out' };
    }

    return { mode: 'add', label: getAddToCartLabel(sectionId) };
  }

  function updateProductCta(sectionId) {
    const productForm = document.getElementById(`product-form-${sectionId}`);
    if (!productForm) return;

    const mainButton = productForm.querySelector('[name="add"]');
    const mainLabel = mainButton?.querySelector('span');
    const stickyRoot = document.getElementById(`sticky-cta-${sectionId}`);
    const stickyButton = stickyRoot?.querySelector('.sticky-cta__button');
    const stickyLabel = stickyRoot?.querySelector('.sticky-cta__label');

    if (!mainButton || !mainLabel) return;

    const state = resolveCtaState(sectionId);

    applyCtaState(mainButton, mainLabel, state, state.label, sectionId, mainButton);

    if (stickyButton && stickyLabel) {
      applyCtaState(stickyButton, stickyLabel, state, state.label, sectionId, mainButton);
    }
  }

  function initStickyCtaObserver(sectionId) {
    if (observers.has(sectionId)) return;

    const productForm = document.getElementById(`product-form-${sectionId}`);
    const mainButton = productForm?.querySelector('.product-form__submit');
    const stickyRoot = document.getElementById(`sticky-cta-${sectionId}`);

    if (!mainButton || !stickyRoot) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        stickyRoot.classList.toggle('is-visible', !entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(mainButton);
    observers.set(sectionId, observer);
  }

  function getSelectedColor(variantSwitcher) {
    if (!variantSwitcher) return null;

    if (variantSwitcher.tagName === 'VARIANT-RADIOS') {
      for (const fieldset of variantSwitcher.querySelectorAll('fieldset')) {
        if (getOptionNameFromFieldset(fieldset) !== 'Color') continue;
        return fieldset.querySelector('input[type="radio"]:checked')?.value || null;
      }
    }

    if (variantSwitcher.tagName === 'VARIANT-SELECTS') {
      for (const select of variantSwitcher.querySelectorAll('select')) {
        if (getOptionNameFromSelect(select) !== 'Color') continue;
        return select.value || null;
      }
    }

    return null;
  }

  function getColorOptionIndex(variantSwitcher) {
    if (!variantSwitcher) return -1;

    if (variantSwitcher.tagName === 'VARIANT-RADIOS') {
      return Array.from(variantSwitcher.querySelectorAll('fieldset')).findIndex(
        (fieldset) => getOptionNameFromFieldset(fieldset) === 'Color'
      );
    }

    return Array.from(variantSwitcher.querySelectorAll('select')).findIndex(
      (select) => getOptionNameFromSelect(select) === 'Color'
    );
  }

  function formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents);
    }

    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    }).format(cents / 100);
  }

  function isPartialDefaultActive(sectionId) {
    const variantSwitcher = getVariantSwitcher(sectionId);
    return variantSwitcher?.dataset.partialDefault === 'true';
  }

  function updatePartialPrice(sectionId) {
    if (!isPartialDefaultActive(sectionId)) return;

    const variantSwitcher = getVariantSwitcher(sectionId);
    const missingOptions = getMissingOptions(variantSwitcher);

    if (!missingOptions.length) return;

    const colorValue = getSelectedColor(variantSwitcher);
    const colorIndex = getColorOptionIndex(variantSwitcher);
    const priceRoots = document.querySelectorAll(`[data-product-price="${sectionId}"]`);

    if (!colorValue || colorIndex < 0 || !priceRoots.length || !variantSwitcher?.getVariantData) return;

    const matchingVariants = variantSwitcher
      .getVariantData()
      .filter((variant) => variant.options[colorIndex] === colorValue);

    if (!matchingVariants.length) return;

    const availableVariants = matchingVariants.filter((variant) => variant.available);
    const pricePool = availableVariants.length ? availableVariants : matchingVariants;
    const prices = pricePool.map((variant) => variant.price);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const formattedPrice = formatMoney(minPrice);
    const priceHtml =
      minPrice !== maxPrice
        ? (window.variantStrings?.fromPriceHtml || 'From @@PRICE@@').replace('@@PRICE@@', formattedPrice)
        : formattedPrice;

    priceRoots.forEach((priceRoot) => {
      const regularPriceItem = priceRoot.querySelector('.price__regular .price-item--regular');
      if (regularPriceItem) regularPriceItem.innerHTML = priceHtml;

      priceRoot.classList.remove('visibility-hidden');
    });
  }

  function initPartialDefault(sectionId) {
    if (!isPartialDefaultActive(sectionId)) return;

    const hiddenInput = document.querySelector(`#product-form-${sectionId} input[name="id"]`);
    if (hiddenInput) hiddenInput.value = '';

    if (typeof deleteVariants === 'function') deleteVariants();

    updateProductCta(sectionId);
    updatePartialPrice(sectionId);
  }

  function initProductCta(sectionId) {
    const run = () => {
      initPartialDefault(sectionId);
      updateProductCta(sectionId);
      initStickyCtaObserver(sectionId);
    };

    const scheduleRun = () => requestAnimationFrame(run);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', scheduleRun);
    } else {
      scheduleRun();
    }

    window.addEventListener('load', () => {
      initPartialDefault(sectionId);
      updateProductCta(sectionId);
      updatePartialPrice(sectionId);
    });
  }

  window.ProductCtaState = {
    getMissingOptions,
    getSelectLabel,
    updateProductCta,
    initStickyCtaObserver,
    initProductCta,
    initPartialDefault,
    updatePartialPrice,
    scrollToMissingOption,
  };
})();
